'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { cn } from '@/app/utils';
import { AngleRight } from '../../icons/AngleRight';
import type { UserCasesSectionData } from '../../types';
import { MediaAsset } from '../MediaAsset';
import { PortableTextContent } from '../PortableTextContent';
import { SectionShell } from '../section-shared';

export function Carousel({ section }: { section: UserCasesSectionData }) {
  const cases = section.cases ?? [];
  const count = cases.length;

  const UC_GAP = 32;
  const UC_ARROW_SIZE = 48;

  const viewportRef = useRef<HTMLDivElement>(null);
  const [viewportW, setViewportW] = useState(0);
  const [current, setCurrent] = useState(() => count + Math.floor(count / 2));
  const [animate, setAnimate] = useState(false);

  const SIDE_PADDING = 24;
  const CARD_WIDTH = 447;

  const cardW = CARD_WIDTH;
  const totalWidth = count * cardW + UC_GAP * (count - 1) + SIDE_PADDING * 2;
  const shouldCarousel = viewportW > 0 && totalWidth > viewportW && count > 4;

  const [isCarousel, setIsCarousel] = useState(false);
  useEffect(() => {
    if (viewportW > 0) {
      setIsCarousel(shouldCarousel);
    }
  }, [shouldCarousel, viewportW]);

  const slides = useMemo(() => {
    return isCarousel ? [...cases, ...cases, ...cases] : cases;
  }, [cases, isCarousel]);

  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => setViewportW(entry.contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const slideW = cardW + UC_GAP;
  const arrowOffset = cardW / 2 + UC_GAP * 2 + UC_ARROW_SIZE / 2;

  const getOffset = useCallback(
    (idx: number): number => viewportW / 2 - cardW / 2 - idx * slideW,
    [viewportW, cardW, slideW],
  );

  const handlePrev = useCallback(() => {
    if (animate) return;
    setAnimate(true);
    setCurrent((p) => p - 1);
  }, [animate]);

  const handleNext = useCallback(() => {
    if (animate) return;
    setAnimate(true);
    setCurrent((p) => p + 1);
  }, [animate]);

  const handleTransitionEnd = useCallback(() => {
    setAnimate(false);
    setCurrent((prev) => {
      if (prev < count) return prev + count;
      if (prev >= count * 2) return prev - count;
      return prev;
    });
  }, [count]);

  return (
    <SectionShell>
      <div className="w-full text-center">
        <h2 className="font-semibold text-[36px] text-white leading-[1.2] sm:text-[28px]">{section.title}</h2>
        <PortableTextContent className="mt-4" value={section.description} />
      </div>

      <div
        ref={viewportRef}
        className="-translate-x-1/2 relative left-1/2 mt-[42px] w-screen overflow-hidden sm:static sm:mt-8 sm:w-auto sm:translate-x-0 sm:overflow-visible sm:px-4"
      >
        <div className="group relative">
          {isCarousel && (
            <>
              <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-[240px] bg-gradient-to-r from-black to-transparent sm:hidden" />
              <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-[240px] bg-gradient-to-l from-black to-transparent sm:hidden" />
            </>
          )}

          <div
            className="flex sm:flex-col sm:gap-4 sm:[transform:none!important] sm:[transition:none!important]"
            style={{
              gap: `${UC_GAP}px`,
              visibility: viewportW > 0 ? 'visible' : 'hidden',
              ...(isCarousel
                ? {
                    transform: `translateX(${getOffset(current)}px)`,
                    transition: animate ? 'transform 500ms cubic-bezier(0.25, 1, 0.5, 1)' : 'none',
                  }
                : { justifyContent: 'center', padding: `0 ${SIDE_PADDING}px` }),
            }}
            onTransitionEnd={isCarousel ? handleTransitionEnd : undefined}
          >
            {slides?.map((item, i) => {
              const isDuplicate = isCarousel && (i < count || i >= count * 2);
              return (
                <div
                  key={isCarousel ? `${item._key}-${Math.floor(i / count)}` : item._key}
                  className={cn('sm:!w-full aspect-[9/16] shrink-0', isDuplicate && 'sm:hidden')}
                  style={{ width: `${cardW}px` }}
                >
                  <a
                    className="group/item relative block h-full w-full overflow-hidden rounded-2xl border border-white/10 bg-black"
                    href={item.workspaceLink}
                    rel="noreferrer"
                    target="_blank"
                  >
                    <MediaAsset className="h-full w-full object-cover" media={item.media} />
                    <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-[linear-gradient(180deg,rgba(0,0,0,0)_0%,rgba(0,0,0,0.6)_50.149%,rgba(0,0,0,0.8)_100%)] px-4 pt-8 pb-4 opacity-0 transition-opacity duration-300 group-hover/item:opacity-100">
                      <p className="font-medium text-base text-white leading-[1.2]">Prompt</p>
                      <p className="mt-2 text-sm text-white leading-[1.5]">{item.prompt}</p>
                    </div>
                  </a>
                </div>
              );
            })}
          </div>

          {isCarousel && (
            <>
              <button
                className="sm:!hidden -translate-y-1/2 absolute top-1/2 z-20 hidden size-12 items-center justify-center rounded-full bg-black/60 text-white opacity-0 backdrop-blur-[4px] transition-all duration-300 hover:bg-black/40 active:bg-black/80 group-hover:flex group-hover:opacity-100"
                onClick={handlePrev}
                style={{ left: `calc(50% - ${arrowOffset}px)` }}
                type="button"
              >
                <AngleRight className="size-6 rotate-180" />
              </button>
              <button
                className="sm:!hidden -translate-y-1/2 absolute top-1/2 z-20 hidden size-12 items-center justify-center rounded-full bg-black/60 text-white opacity-0 backdrop-blur-[4px] transition-all duration-300 hover:bg-black/40 active:bg-black/80 group-hover:flex group-hover:opacity-100"
                onClick={handleNext}
                style={{ right: `calc(50% - ${arrowOffset}px)` }}
                type="button"
              >
                <AngleRight className="size-6" />
              </button>
            </>
          )}
        </div>
      </div>
    </SectionShell>
  );
}
